import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, model, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { PropertyDto } from '@workspace/data-types';
import { MissingPropertyInfo, PropertyMissingInfoUtils } from '../../utils/property-missing-info.utils';

@Component({
  selector: 'etxe-property-missing-info',
  imports: [CommonModule, RouterLink, MatButtonModule, MatIconModule],
  templateUrl: './property-missing-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EtxePropertyMissingInfoComponent {
  property = input.required<PropertyDto>();
  variant = input<'card' | 'drawer'>('card');
  mode = input<'edit' | 'view'>('view');

  // By default, false (closed)
  expanded = model<boolean>(false);

  actionClicked = output<MissingPropertyInfo>();

  readonly activeFilter = signal<'all' | 'critical' | 'warning' | 'info'>('all');
  readonly totalEvaluatedChecks = PropertyMissingInfoUtils.TOTAL_EVALUATED_CHECKS;

  readonly missingItems = computed<MissingPropertyInfo[]>(() => {
    return PropertyMissingInfoUtils.getAllMissingItems(this.property());
  });

  readonly completedChecks = computed(() =>
    Math.max(0, this.totalEvaluatedChecks - this.missingItems().length),
  );

  readonly completionPercentage = computed(() =>
    Math.round((this.completedChecks() / this.totalEvaluatedChecks) * 100),
  );

  readonly criticalCount = computed(
    () => this.missingItems().filter((i) => i.severity === 'critical').length,
  );
  readonly warningCount = computed(() => this.missingItems().filter((i) => i.severity === 'warning').length);
  readonly infoCount = computed(() => this.missingItems().filter((i) => i.severity === 'info').length);

  readonly filteredMissingItems = computed(() => {
    const filter = this.activeFilter();
    const items = this.missingItems();
    if (filter === 'all') return items;
    return items.filter((i) => i.severity === filter);
  });

  toggleExpanded(): void {
    this.expanded.update((v) => !v);
  }

  setFilter(filter: 'all' | 'critical' | 'warning' | 'info'): void {
    this.activeFilter.set(filter);
  }

  handleAction(item: MissingPropertyInfo): void {
    if (this.mode() === 'edit') {
      this.expanded.set(false);
      this.actionClicked.emit(item);
      const el = document.getElementById(item.anchorId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }
}
